import { useEffect, useState } from 'react'
import { isOrgSharedAccountEmail, getOuCodeFromEmail } from 'utils/orgSharedAccount'
import ouCalendarService from 'services/ouCalendar'

/**
 * 회사캘린더(조직 공용 계정) 소유자 이메일로부터 OU 이름을 조회한다.
 * org shared 이메일이 아니면 undefined.
 * OU 목록이 아직 로드되지 않았으면 이 hook 호출을 트리거로 lazy 로드하고,
 * 완료 시 리렌더를 위해 state를 갱신한다.
 * 매칭 실패/로드 전/에러 시에는 undefined를 반환 — 호출부가 기존 고정 라벨로 fallback해야 한다.
 */
const useOrgSharedOwnerName = (ownerEmail?: string | null): string | undefined => {
  const isOrgShared = isOrgSharedAccountEmail(ownerEmail)
  const ouCode = isOrgShared ? getOuCodeFromEmail(ownerEmail) : null

  const [ouName, setOuName] = useState<string | undefined>(() =>
    ouCode ? ouCalendarService.getOuName(ouCode) : undefined,
  )

  useEffect(() => {
    if (!ouCode) {
      setOuName(undefined)
      return
    }

    const cached = ouCalendarService.getOuName(ouCode)
    if (cached != null) {
      setOuName(cached)
      return
    }

    let cancelled = false
    const unsubscribe = ouCalendarService.subscribe(() => {
      if (cancelled) return
      setOuName(ouCalendarService.getOuName(ouCode))
    })
    ouCalendarService.load()

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [ouCode])

  return ouName
}

export default useOrgSharedOwnerName
